// src/components/dashboards/FacultyDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { facultyAPI, departmentAPI, classAPI, classAttendanceAPI, mbbsAPI, facultyThesisProposalAPI } from '../../services/api';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import '../../styles/Dashboard.css';
import '../../styles/ModalStyles.css';
import LeaveManagementSidebar from '../leave/LeaveManagementSidebar';
import FacultyCMReviews from './FacultyCMReviews';
import StudentActivityModal from '../professor/activities/modals/StudentActivityModal';

import LeaveBalanceSummary from '../leave/LeaveBalanceSummary';
import Calendar from '../shared/Calendar';
import DailyExpensesTab from './DailyExpensesTab';
import OPDConsultationTab from './OPDConsultationTab';
import SubmitAttendance from '../attendance/SubmitAttendance';
import SalarySlipRequest from '../admin/SalarySlipRequest';
import { ClassTimetableView, ClassCalendarView } from '../shared/ClassTimetableCalendar';
import { exportTimetableToPDF } from '../../utils/timetablePDF';
import MyBorrowedBooks from '../shared/MyBorrowedBooks';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import { hostelInventoryAPI } from '../../services/api';

/* ─────────────────────────────────────────────────────────────────────────────
   Constants & helpers (from File 2)
───────────────────────────────────────────────────────────────────────────── */
const STATUS_META = {
  PENDING:   { color: '#92400e', bg: '#fef3c7', label: 'Pending',   icon: '⏳', next: 'ONGOING',   nextLabel: '▶ Start Class' },
  ONGOING:   { color: '#1d4ed8', bg: '#dbeafe', label: 'Ongoing',   icon: '▶', next: 'COMPLETED', nextLabel: '✓ Mark Completed' },
  COMPLETED: { color: '#166534', bg: '#dcfce7', label: 'Completed', icon: '✓', next: null,         nextLabel: null },
  CANCELLED: { color: '#6b7280', bg: '#f3f4f6', label: 'Cancelled', icon: '✕', next: null,         nextLabel: null },
};

const MBBS_YEAR_LABELS = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year', 4: '4th Year' };

const fmtDate = s => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDT   = s => s ? new Date(s).toLocaleString('en-IN',  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/* ─────────────────────────────────────────────────────────────────────────────
   StatusBadge (from File 2)
───────────────────────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.PENDING;
  return (
    <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Modal inline styles (from File 2)
───────────────────────────────────────────────────────────────────────────── */
const MD = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  modal:     { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 24px 60px #0005' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid #f1f5f9' },
  title:     { fontSize: 17, fontWeight: 800, color: '#0f172a', margin: 0 },
  close:     { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 },
  info:      { margin: '16px 20px', background: '#f8fafc', borderRadius: 10, padding: 14 },
  err:       { margin: '0 20px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13 },
  lbl:       { display: 'block', marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.04em' },
  footer:    { display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid #f1f5f9' },
  cancelBtn: { padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' },
  saveBtn:   { padding: '9px 22px', borderRadius: 8, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

/* ─────────────────────────────────────────────────────────────────────────────
   StatusUpdateModal (from File 2)
───────────────────────────────────────────────────────────────────────────── */
function StatusUpdateModal({ cs, onClose, onUpdated }) {
  const sm     = STATUS_META[cs.status];
  const nextSt = sm?.next;
  const [note,          setNote]         = useState('');
  const [saving,        setSaving]       = useState(false);
  const [error,         setError]        = useState('');
  const [customStatus,  setCustomStatus] = useState(nextSt || 'ONGOING');

  // Attendance state — only used when marking COMPLETED
  const [students,   setStudents]   = useState([]);
  const [markMap,    setMarkMap]    = useState({});
  const [loadingStd, setLoadingStd] = useState(false);

  const ATT_COLOR = {
    PRESENT: { c: '#166534', bg: '#dcfce7', border: '#86efac' },
    ABSENT:  { c: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
    LATE:    { c: '#854d0e', bg: '#fef3c7', border: '#fde047' },
    EXCUSED: { c: '#4c1d95', bg: '#ede9fe', border: '#c4b5fd' },
  };
  const STATUS_OPTIONS = ['PRESENT', 'ABSENT', 'LATE', 'EXCUSED'];

  // Load students whenever the chosen status becomes COMPLETED
  useEffect(() => {
    if (customStatus !== 'COMPLETED') return;
    setLoadingStd(true);
    mbbsAPI.getFacultyMBBSStudents()
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results || []);
        setStudents(list);
        const map = {};
        list.forEach(s => { map[s.id] = 'PRESENT'; });
        setMarkMap(map);
      })
      .catch(() => setError('Could not load students.'))
      .finally(() => setLoadingStd(false));
  }, [customStatus]);

  if (!nextSt) return null;

  const handleUpdate = async () => {
    setSaving(true); setError('');
    try {
      // If completing the class, save attendance first
      if (customStatus === 'COMPLETED' && students.length > 0) {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
        const records = students.map(s => ({
          student_id: s.id,
          status: markMap[s.id] || 'PRESENT',
          notes: '',
        }));
        await classAttendanceAPI.markBulk({
          class_id: cs.id,
          date: dateStr,
          records,
        });
      }
      await classAPI.updateStatus(cs.id, { status: customStatus, status_note: note });
      onUpdated();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update status.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStudent = (id) =>
    setMarkMap(m => ({ ...m, [id]: m[id] === 'PRESENT' ? 'ABSENT' : 'PRESENT' }));

  const presentCount = Object.values(markMap).filter(v => v === 'PRESENT').length;
  const absentCount  = Object.values(markMap).filter(v => v === 'ABSENT').length;
  const lateCount    = Object.values(markMap).filter(v => v === 'LATE').length;

  // Wider modal when showing attendance
  const isCompleting = customStatus === 'COMPLETED';
  const modalStyle   = {
    ...MD.modal,
    maxWidth: isCompleting ? 700 : 480,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <div style={MD.overlay} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={MD.header}>
          <h3 style={MD.title}>Update Class Status</h3>
          <button style={MD.close} onClick={onClose}>×</button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

          {/* Class info */}
          <div style={MD.info}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>{cs.title}</div>
            {cs.topic && (
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1d4ed8', marginBottom: 6 }}>
                📖 Topic to be Taught: <span style={{ color: '#0f172a' }}>{cs.topic}</span>
              </div>
            )}
            <div style={{ fontSize: 12, color: '#64748b' }}>{MBBS_YEAR_LABELS[cs.mbbs_year]} · {cs.department_name}</div>
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <StatusBadge status={cs.status} />
              <span style={{ fontSize: 13, color: '#94a3b8' }}>→</span>
              <StatusBadge status={customStatus} />
            </div>
          </div>

          {error && <div style={MD.err}>{error}</div>}

          {/* Status choice */}
          <div style={{ padding: '0 20px 14px' }}>
            <label style={MD.lbl}>New Status</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['ONGOING', 'COMPLETED'].filter(s => {
                if (cs.status === 'PENDING') return true;
                if (cs.status === 'ONGOING') return s === 'COMPLETED';
                return false;
              }).map(s => {
                const m = STATUS_META[s];
                return (
                  <button key={s} onClick={() => setCustomStatus(s)}
                    style={{
                      padding: '8px 18px', borderRadius: 8,
                      border: `2px solid ${customStatus === s ? m.color : '#e2e8f0'}`,
                      background: customStatus === s ? m.bg : '#fff',
                      color: customStatus === s ? m.color : '#374151',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}>
                    {m.icon} {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Attendance panel — shown only when marking COMPLETED ── */}
          {isCompleting && (
            <div style={{ margin: '0 20px 16px', border: '1.5px solid #e0f2fe', borderRadius: 12, overflow: 'hidden', background: '#f0f9ff' }}>

              {/* Panel header */}
              <div style={{ padding: '12px 16px', background: '#0ea5e9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>
                  📋 Mark Today's Attendance — {MBBS_YEAR_LABELS[cs.mbbs_year]}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534' }}>✓ {presentCount}</span>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#991b1b' }}>✗ {absentCount}</span>
                  {lateCount > 0 && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#854d0e' }}>⏱ {lateCount}</span>}
                </div>
              </div>

              {loadingStd ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#0369a1', fontSize: 13 }}>Loading students…</div>
              ) : students.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#64748b', fontSize: 13 }}>No MBBS students found in your department.</div>
              ) : (
                <>
                  {/* Quick-action bar */}
                  <div style={{ padding: '8px 14px', borderBottom: '1px solid #bae6fd', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: '#e0f2fe' }}>
                    <span style={{ fontSize: 11, color: '#0369a1', fontWeight: 700 }}>Quick mark:</span>
                    <button onClick={() => { const m = {}; students.forEach(s => { m[s.id] = 'PRESENT'; }); setMarkMap(m); }}
                      style={{ padding: '4px 10px', border: '1px solid #86efac', borderRadius: 6, background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      ✓ All Present
                    </button>
                    <button onClick={() => { const m = {}; students.forEach(s => { m[s.id] = 'ABSENT'; }); setMarkMap(m); }}
                      style={{ padding: '4px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fef2f2', color: '#991b1b', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      ✗ All Absent
                    </button>
                    <span style={{ fontSize: 10, color: '#64748b' }}>Click a card to toggle P/A</span>
                  </div>

                  {/* Detailed override table */}
                  <div style={{ padding: '0 14px 14px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Override individual statuses</div>
                    <div style={{ border: '1px solid #bae6fd', borderRadius: 8, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#e0f2fe' }}>
                          <tr>
                            {['Student', 'Status'].map(h => (
                              <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((s, i) => {
                            const cur = markMap[s.id] || 'PRESENT';
                            const ac  = ATT_COLOR[cur];
                            return (
                              <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#f0f9ff', borderBottom: '1px solid #e0f2fe' }}>
                                <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                                  {s.first_name} {s.last_name}
                                  <span style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>{s.profile?.student_id || ''}</span>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    {STATUS_OPTIONS.map(o => {
                                      const oa = ATT_COLOR[o];
                                      return (
                                        <button key={o} onClick={() => setMarkMap(m => ({ ...m, [s.id]: o }))}
                                          style={{ padding: '3px 8px', borderRadius: 6, border: `1px solid ${cur === o ? oa.c : '#e2e8f0'}`, background: cur === o ? oa.bg : '#fff', color: cur === o ? oa.c : '#94a3b8', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                                          {o[0]}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Note field */}
          <div style={{ padding: '0 20px 16px' }}>
            <label style={MD.lbl}>
              Status Note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={
                customStatus === 'ONGOING'
                  ? 'e.g. Started Chapter 1 — Gross Anatomy'
                  : 'e.g. All chapters covered, evaluation conducted'
              }
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, height: 72, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={MD.footer}>
          <button onClick={onClose} style={MD.cancelBtn}>Cancel</button>
          <button
            onClick={handleUpdate}
            disabled={saving || (isCompleting && loadingStd)}
            style={{ ...MD.saveBtn, background: STATUS_META[customStatus]?.color || '#1d4ed8', opacity: (saving || (isCompleting && loadingStd)) ? .6 : 1 }}
          >
            {saving
              ? 'Saving…'
              : isCompleting && students.length > 0
                ? `✓ Save Attendance & Complete`
                : `${STATUS_META[customStatus]?.icon} Mark as ${STATUS_META[customStatus]?.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   FacultyClassCard (from File 2)
───────────────────────────────────────────────────────────────────────────── */
function FacultyClassCard({ cs, onUpdateClick }) {
  const sm     = STATUS_META[cs.status] || STATUS_META.PENDING;
  const canAct = sm.next !== null;

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${cs.status === 'ONGOING' ? '#bfdbfe' : '#e5e7eb'}`,
      borderRadius: 14, padding: '18px 20px',
      display: 'flex', flexDirection: 'column', gap: 12,
      boxShadow: cs.status === 'ONGOING' ? '0 0 0 3px #dbeafe' : 'none',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 3 }}>{cs.title}</div>
          {cs.topic ? (
            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: 8, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              📖 Topic: <span style={{ color: '#0f172a' }}>{cs.topic}</span>
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic', marginTop: 3 }}>
              📖 Topic: General Session (No topic specified)
            </div>
          )}
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {MBBS_YEAR_LABELS[cs.mbbs_year]} · <strong>{cs.department_name}</strong> · {cs.duration_display}
          </div>
        </div>
        <StatusBadge status={cs.status} />
      </div>

      {/* Details grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10 }}>
        {[
          ['📅 Start',       fmtDate(cs.start_date)],
          ['🏁 End',         fmtDate(cs.end_date)],
          ['👤 Assigned by', cs.assigned_by_hod_name || cs.assigned_by_name || '—'],
          ['📋 Created by',  cs.assigned_by_name || '—'],
        ].map(([l, v]) => (
          <div key={l} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>{l}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Timestamps */}
      {(cs.started_at || cs.completed_at) && (
        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#475569', flexWrap: 'wrap' }}>
          {cs.started_at   && <span>▶ <strong>Started:</strong> {fmtDT(cs.started_at)}</span>}
          {cs.completed_at && <span>✓ <strong>Completed:</strong> {fmtDT(cs.completed_at)}</span>}
        </div>
      )}

      {/* Notes */}
      {cs.schedule_notes && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#78350f' }}>
          📝 {cs.schedule_notes}
        </div>
      )}
      {cs.status_note && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#0c4a6e' }}>
          💬 Last update: {cs.status_note}
        </div>
      )}

      {/* Action button */}
      {canAct && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={() => onUpdateClick(cs)}
            style={{
              padding: '9px 22px', borderRadius: 9, border: 'none',
              background: cs.status === 'PENDING'
                ? 'linear-gradient(135deg,#1d4ed8,#2563eb)'
                : 'linear-gradient(135deg,#059669,#10b981)',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {sm.nextLabel}
          </button>
        </div>
      )}

      {cs.status === 'COMPLETED' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#166534', fontWeight: 700, fontSize: 13 }}>
          <span style={{ fontSize: 18 }}>🎉</span> Class completed — great work!
        </div>
      )}
    </div>
  );
}

// ─── Faculty Class Attendance Tab ─────────────────────────────────────────────
export function FacultyClassAttendanceTab() {
  const [classes,       setClasses]       = useState([]);
  const [selClass,      setSelClass]      = useState('');
  const [selDate,       setSelDate]       = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [students,      setStudents]      = useState([]);
  const [markMap,       setMarkMap]       = useState({});
  const [records,       setRecords]       = useState([]);
  const [pendingSelf,   setPendingSelf]   = useState([]);
  const [historyRecords,setHistoryRecords]= useState([]);
  const [loadingCls,    setLoadingCls]    = useState(true);
  const [loadingStudents,setLoadingStudents]= useState(false);
  const [loadingHistory, setLoadingHistory]= useState(false);
  const [saving,        setSaving]        = useState(false);
  const [confirming,    setConfirming]    = useState(false);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');
  const [view,          setView]          = useState('mark');
  const [historyDate,   setHistoryDate]   = useState('');

  const STATUS_OPTIONS = ['PRESENT', 'ABSENT', 'LATE', 'EXCUSED'];
  const ATT_COLOR = {
    PRESENT: { c: '#166534', bg: '#dcfce7', border: '#86efac' },
    ABSENT:  { c: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
    LATE:    { c: '#854d0e', bg: '#fef3c7', border: '#fde047' },
    EXCUSED: { c: '#4c1d95', bg: '#ede9fe', border: '#c4b5fd' },
  };

  // Load class list once — no duplicate call
  useEffect(() => {
    classAPI.getFacultyClasses({})
      .then(d => setClasses(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => {})
      .finally(() => setLoadingCls(false));
  }, []);

  const loadStudentsAndExisting = useCallback(async () => {
    if (!selClass || !selDate) return;
    setLoadingStudents(true); setError(''); setSuccess('');
    try {
      const [stuData, attData] = await Promise.all([
        mbbsAPI.getFacultyMBBSStudents(),
        classAttendanceAPI.getForClass(selClass, selDate),
      ]);
      const stuList = Array.isArray(stuData) ? stuData : (stuData.results || []);
      const allAtt  = Array.isArray(attData) ? attData : [];
      setStudents(stuList);
      setRecords(allAtt);
      setPendingSelf(allAtt.filter(r => r.is_self_marked && !r.is_confirmed));
      const map = {};
      stuList.forEach(s => {
        const ex = allAtt.find(r => r.student === s.id);
        map[s.id] = ex ? ex.status : 'PRESENT';
      });
      setMarkMap(map);
    } catch { setError('Failed to load attendance data.'); }
    finally  { setLoadingStudents(false); }
  }, [selClass, selDate]);

  useEffect(() => { loadStudentsAndExisting(); }, [loadStudentsAndExisting]);

  const loadHistory = useCallback(async () => {
    if (!selClass || !historyDate) return;
    setLoadingHistory(true); setError('');
    try {
      const data = await classAttendanceAPI.getForClass(selClass, historyDate);
      setHistoryRecords(Array.isArray(data) ? data : []);
    } catch { setHistoryRecords([]); }
    finally  { setLoadingHistory(false); }
  }, [selClass, historyDate]);

  useEffect(() => { if (view === 'history') loadHistory(); }, [historyDate, view, loadHistory]);

  const markAllPresent = () => { const m = {}; students.forEach(s => { m[s.id] = 'PRESENT'; }); setMarkMap(m); };
  const markAllAbsent  = () => { const m = {}; students.forEach(s => { m[s.id] = 'ABSENT';  }); setMarkMap(m); };
  const toggleStudent  = (id) => setMarkMap(m => ({ ...m, [id]: m[id] === 'PRESENT' ? 'ABSENT' : 'PRESENT' }));

  const handleBulkSave = async () => {
    if (!selClass || !selDate || students.length === 0) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      const recs = students.map(s => ({ student_id: s.id, status: markMap[s.id] || 'ABSENT', notes: '' }));
      await classAttendanceAPI.markBulk({ class_id: parseInt(selClass), date: selDate, records: recs });
      setSuccess(`Attendance saved for ${students.length} student(s).`);
      loadStudentsAndExisting();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save attendance.');
    } finally { setSaving(false); }
  };

  const handleConfirm = async (ids, action) => {
    setConfirming(true); setError(''); setSuccess('');
    try {
      const res = await classAttendanceAPI.confirmSelf({ attendance_ids: ids, action });
      setSuccess(res.message || 'Done.');
      loadStudentsAndExisting();
    } catch { setError('Failed to update confirmation.'); }
    finally  { setConfirming(false); }
  };

  const selectedClassObj = classes.find(c => c.id == selClass);

  const presentCount = Object.values(markMap).filter(v => v === 'PRESENT').length;
  const absentCount  = Object.values(markMap).filter(v => v === 'ABSENT').length;
  const lateCount    = Object.values(markMap).filter(v => v === 'LATE').length;
  const excusedCount = Object.values(markMap).filter(v => v === 'EXCUSED').length;
  const totalStudents = students.length;

  // History analytics
  const hPresent = historyRecords.filter(r => r.status === 'PRESENT' || r.status === 'LATE').length;
  const hAbsent  = historyRecords.filter(r => r.status === 'ABSENT').length;
  const hTotal   = historyRecords.length;
  const hPct     = hTotal > 0 ? Math.round(hPresent / hTotal * 100) : 0;

  // ── Attendance Donut chart via SVG ──────────────────────────────────────
  const AttendanceDonut = ({ present, absent, late, excused, total }) => {
    if (total === 0) return null;
    const segments = [
      { value: present, color: '#16a34a', label: 'Present' },
      { value: absent,  color: '#dc2626', label: 'Absent'  },
      { value: late,    color: '#d97706', label: 'Late'    },
      { value: excused, color: '#7c3aed', label: 'Excused' },
    ].filter(s => s.value > 0);

    const R = 52, stroke = 20, cx = 70, cy = 70;
    const circumference = 2 * Math.PI * R;
    let offset = 0;
    const slices = segments.map(s => {
      const dash = (s.value / total) * circumference;
      const gap  = circumference - dash;
      const slice = { ...s, dash, gap, offset };
      offset += dash;
      return slice;
    });

    const pct = Math.round((present / total) * 100);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        {/* Donut */}
        <svg width="140" height="140" style={{ flexShrink: 0 }}>
          {/* Track */}
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
          {slices.map((s, i) => (
            <circle key={i} cx={cx} cy={cy} r={R} fill="none"
              stroke={s.color} strokeWidth={stroke}
              strokeDasharray={`${s.dash} ${s.gap}`}
              strokeDashoffset={-s.offset + circumference * 0.25}
              style={{ transition: 'stroke-dasharray .6s ease' }}
            />
          ))}
          {/* Centre label */}
          <text x={cx} y={cy - 6} textAnchor="middle" fill="#0f172a" fontSize="20" fontWeight="800" fontFamily="DM Sans,sans-serif">{pct}%</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fill="#64748b" fontSize="10" fontFamily="DM Sans,sans-serif">attendance</text>
        </svg>

        {/* Legend */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: 'Present', value: present, color: '#16a34a', bg: '#dcfce7' },
            { label: 'Absent',  value: absent,  color: '#dc2626', bg: '#fef2f2' },
            { label: 'Late',    value: late,     color: '#d97706', bg: '#fef3c7' },
            { label: 'Excused', value: excused,  color: '#7c3aed', bg: '#ede9fe' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#374151', fontWeight: 600, minWidth: 52 }}>{item.label}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: item.color, padding: '1px 8px', background: item.bg, borderRadius: 6 }}>{item.value}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{total > 0 ? Math.round(item.value / total * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Horizontal bar chart — per-student visual ───────────────────────────
  const AttendanceBarList = ({ students, markMap }) => {
    if (students.length === 0) return null;
    const ATT_BAR = {
      PRESENT: { color: '#16a34a', bg: '#dcfce7', width: '100%' },
      ABSENT:  { color: '#dc2626', bg: '#fef2f2', width: '100%' },
      LATE:    { color: '#d97706', bg: '#fef3c7', width: '100%' },
      EXCUSED: { color: '#7c3aed', bg: '#ede9fe', width: '100%' },
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {students.map(s => {
          const st = markMap[s.id] || 'PRESENT';
          const bar = ATT_BAR[st];
          return (
            <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 60px', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.first_name} {s.last_name}
              </span>
              <div style={{ height: 20, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: bar.width, background: bar.color, borderRadius: 4, transition: 'width .4s' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: bar.color, padding: '2px 8px', background: bar.bg, borderRadius: 5, textAlign: 'center' }}>
                {st[0]}{st.slice(1).toLowerCase()}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const S = {
    tab: (active) => ({
      padding: '7px 14px', borderRadius: 8,
      border: `1.5px solid ${active ? '#1d4ed8' : '#e2e8f0'}`,
      background: active ? '#dbeafe' : '#fff',
      color: active ? '#1d4ed8' : '#374151',
      fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    }),
    card:   { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' },
    header: { padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
    th:     { padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    td:     { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    actionBtn: (bg, border, color) => ({ padding: '5px 12px', border: `1px solid ${border}`, borderRadius: 7, background: bg, color, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }),
  };

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Controls row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>Class</label>
          {loadingCls ? <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</div> : (
            <select value={selClass} onChange={e => { setSelClass(e.target.value); setMarkMap({}); }}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#0f172a', outline: 'none', fontFamily: 'inherit' }}>
              <option value="">— Select class —</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.title} · {MBBS_YEAR_LABELS[c.mbbs_year]} · {STATUS_META[c.status]?.label || c.status}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>Date</label>
          <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#0f172a', outline: 'none', fontFamily: 'inherit' }} />
        </div>
      </div>

      {/* Class info banner */}
      {selClass && selectedClassObj && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 16px', fontSize: 13, display: 'flex', gap: 20, flexWrap: 'wrap', color: '#1d4ed8' }}>
          <span><strong>{selectedClassObj.title}</strong></span>
          <span>Year: <strong>{MBBS_YEAR_LABELS[selectedClassObj.mbbs_year]}</strong></span>
          <span>Dept: <strong>{selectedClassObj.department_name}</strong></span>
          <span>Status: <StatusBadge status={selectedClassObj.status} /></span>
        </div>
      )}

      {/* Sub-tab navigation — Analytics removed */}
      {selClass && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { key: 'mark',    label: `✓ Attendance` },
            { key: 'history', label: '📅 Date-wise History' },
            { key: 'pending', label: `⏱ Pending (${pendingSelf.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setView(t.key)} style={S.tab(view === t.key)}>{t.label}</button>
          ))}
        </div>
      )}

      {error   && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>{success}</div>}

      {!selClass ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          Select a class above to manage attendance.
        </div>
      ) : loadingStudents ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading…</div>

      ) : view === 'mark' ? (
        /* ─────────── MARK ATTENDANCE ─────────── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Graphical summary card ── */}
          {students.length > 0 && (
            <div style={{ ...S.card, padding: '20px 24px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 18 }}>
                📊 Attendance Summary — <span style={{ color: '#64748b', fontWeight: 400 }}>{selDate} · {totalStudents} students</span>
              </div>
              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {/* Donut */}
                <AttendanceDonut
                  present={presentCount} absent={absentCount}
                  late={lateCount} excused={excusedCount}
                  total={totalStudents}
                />

                {/* Threshold bar */}
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, marginBottom: 6, color: '#374151' }}>
                    <span>Attendance Rate</span>
                    <span style={{ color: presentCount / totalStudents >= .75 ? '#16a34a' : '#dc2626' }}>
                      {Math.round(presentCount / totalStudents * 100)}%
                    </span>
                  </div>
                  <div style={{ position: 'relative', height: 18, background: '#f1f5f9', borderRadius: 9, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ height: '100%', width: `${Math.round(presentCount / totalStudents * 100)}%`, background: presentCount / totalStudents >= .75 ? '#16a34a' : '#dc2626', borderRadius: 9, transition: 'width .5s' }} />
                    {/* 75% marker */}
                    <div style={{ position: 'absolute', top: 0, left: '75%', width: 2, height: '100%', background: '#f59e0b' }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {presentCount / totalStudents >= .75
                      ? '✓ Above 75% threshold'
                      : `⚠ ${Math.ceil(totalStudents * .75 - presentCount)} more needed for 75%`}
                  </div>

                  {/* Mini stat row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 14 }}>
                    {[
                      { label: 'Present', value: presentCount, color: '#16a34a', bg: '#dcfce7' },
                      { label: 'Absent',  value: absentCount,  color: '#dc2626', bg: '#fef2f2' },
                      { label: 'Late',    value: lateCount,    color: '#d97706', bg: '#fef3c7' },
                      { label: 'Excused', value: excusedCount, color: '#7c3aed', bg: '#ede9fe' },
                    ].map(item => (
                      <div key={item.label} style={{ background: item.bg, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: item.color, lineHeight: 1 }}>{item.value}</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: item.color, marginTop: 2 }}>{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Per-student bar list */}
              {students.length <= 40 && (
                <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>Per-student breakdown</div>
                  <AttendanceBarList students={students} markMap={markMap} />
                </div>
              )}
            </div>
          )}


        </div>

      ) : view === 'history' ? (
        /* ─────────── DATE-WISE HISTORY ─────────── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>Select Date</label>
              <input type="date" value={historyDate} onChange={e => setHistoryDate(e.target.value)}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#0f172a', outline: 'none', fontFamily: 'inherit' }} />
            </div>
            {historyDate && <button onClick={loadHistory} style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12 }}>↻ Load</button>}
          </div>

          {!historyDate ? (
            <div style={{ textAlign: 'center', padding: '36px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
              Pick a date above to view attendance records for that day.
            </div>
          ) : loadingHistory ? (
            <div style={{ textAlign: 'center', padding: 36, color: '#64748b' }}>Loading…</div>
          ) : historyRecords.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
              No attendance records found for {historyDate}.
            </div>
          ) : (
            <>
              {/* Graphical summary for history */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
                  📊 Attendance Summary — <span style={{ color: '#64748b', fontWeight: 400 }}>{historyDate}</span>
                </div>
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <AttendanceDonut
                    present={hPresent}
                    absent={hAbsent}
                    late={historyRecords.filter(r => r.status === 'LATE').length}
                    excused={historyRecords.filter(r => r.status === 'EXCUSED').length}
                    total={hTotal}
                  />
                  <div style={{ flex: '1 1 200px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                      <span style={{ color: '#374151' }}>Attendance Rate</span>
                      <span style={{ color: hPct >= 75 ? '#16a34a' : '#dc2626' }}>{hPct}%</span>
                    </div>
                    <div style={{ position: 'relative', height: 18, background: '#f1f5f9', borderRadius: 9, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ height: '100%', width: `${hPct}%`, background: hPct >= 75 ? '#16a34a' : '#dc2626', borderRadius: 9 }} />
                      <div style={{ position: 'absolute', top: 0, left: '75%', width: 2, height: '100%', background: '#f59e0b' }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{hPct >= 75 ? '✓ Above 75% threshold' : `⚠ Below 75% minimum`}</div>
                  </div>
                </div>
              </div>

              {/* Present list */}
              <div style={S.card}>
                <div style={{ ...S.header, background: '#f0fdf4' }}>
                  <span style={{ color: '#166534' }}>✓ Present Students</span>
                  <span style={{ fontSize: 12, color: '#166534' }}>{hPresent} students</span>
                </div>
                <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
                  {historyRecords.filter(r => r.status === 'PRESENT' || r.status === 'LATE').map(r => {
                    const ac = ATT_COLOR[r.status] || ATT_COLOR.PRESENT;
                    return (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: `1px solid ${ac.border}`, background: ac.bg }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.student_name}</span>
                        {r.status === 'LATE' && <span style={{ fontSize: 10, fontWeight: 800, color: '#854d0e', marginLeft: 'auto' }}>LATE</span>}
                      </div>
                    );
                  })}
                  {hPresent === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>No present students.</div>}
                </div>
              </div>

              {/* Absent list */}
              <div style={S.card}>
                <div style={{ ...S.header, background: '#fef2f2' }}>
                  <span style={{ color: '#991b1b' }}>✗ Absent Students</span>
                  <span style={{ fontSize: 12, color: '#991b1b' }}>{hAbsent} students</span>
                </div>
                <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
                  {historyRecords.filter(r => r.status === 'ABSENT').map(r => (
                    <div key={r.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.student_name}</div>
                  ))}
                  {hAbsent === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>No absent students.</div>}
                </div>
              </div>

              {/* Excused list */}
              {historyRecords.filter(r => r.status === 'EXCUSED').length > 0 && (
                <div style={S.card}>
                  <div style={{ ...S.header, background: '#ede9fe' }}>
                    <span style={{ color: '#4c1d95' }}>Excused Students</span>
                  </div>
                  <div style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
                    {historyRecords.filter(r => r.status === 'EXCUSED').map(r => (
                      <div key={r.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #c4b5fd', background: '#ede9fe', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.student_name}</div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      ) : view === 'pending' ? (
        /* ─────────── PENDING SELF-MARKED ─────────── */
        <div style={S.card}>
          <div style={S.header}>
            <span>Student Self-Marked — Awaiting Confirmation</span>
            {pendingSelf.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => handleConfirm(pendingSelf.map(r => r.id), 'confirm')} disabled={confirming} style={S.actionBtn('#f0fdf4', '#86efac', '#166534')}>✓ Confirm All</button>
                <button onClick={() => handleConfirm(pendingSelf.map(r => r.id), 'reject')}  disabled={confirming} style={S.actionBtn('#fef2f2', '#fecaca', '#991b1b')}>✗ Reject All</button>
              </div>
            )}
          </div>
          {pendingSelf.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 36, color: '#94a3b8', fontSize: 13 }}>No pending self-marked attendance for {selDate}.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>{['Student', 'Marked Status', 'Marked At', 'Action'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {pendingSelf.map((r, i) => {
                    const ac = ATT_COLOR[r.status] || ATT_COLOR.PRESENT;
                    return (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...S.td, fontWeight: 600 }}>{r.student_name}</td>
                        <td style={S.td}><span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: ac.c, background: ac.bg }}>{r.status_display}</span></td>
                        <td style={{ ...S.td, fontSize: 12, color: '#64748b' }}>{r.marked_at ? new Date(r.marked_at).toLocaleTimeString('en-IN') : '—'}</td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => handleConfirm([r.id], 'confirm')} disabled={confirming} style={S.actionBtn('#f0fdf4', '#86efac', '#166534')}>✓</button>
                            <button onClick={() => handleConfirm([r.id], 'reject')}  disabled={confirming} style={S.actionBtn('#fef2f2', '#fecaca', '#991b1b')}>✗</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   FacultyClassesTab (from File 2) — replaces the old "classes" tab content
───────────────────────────────────────────────────────────────────────────── */
export function FacultyClassesTab() {
    const [classes,      setClasses]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updateTarget, setUpdateTarget] = useState(null);
  const [viewMode,     setViewMode]     = useState('timetable');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await classAPI.getFacultyClasses(params);
      setClasses(Array.isArray(data) ? data : (data.results || []));
    } catch {
      setError('Failed to load assigned classes.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Counts
  const counts = { PENDING: 0, ONGOING: 0, COMPLETED: 0 };
  classes.forEach(c => { if (counts[c.status] !== undefined) counts[c.status]++; });

  const displayed = statusFilter === 'all' ? classes : classes.filter(c => c.status === statusFilter);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Summary chips + PDF export */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>
        {[
          ['Total',     classes.length,    '#334155', '#f1f5f9'],
          ['Pending',   counts.PENDING,    '#92400e', '#fef3c7'],
          ['Ongoing',   counts.ONGOING,    '#1d4ed8', '#dbeafe'],
          ['Completed', counts.COMPLETED,  '#166534', '#dcfce7'],
        ].map(([l, v, c, bg]) => (
          <div key={l} style={{ flex: '1 1 90px', background: bg, borderRadius: 12, padding: '12px 16px' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: c, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>  {/* end inner flex */}
        {!loading && classes.length > 0 && (
          <button
            onClick={() => exportTimetableToPDF({ classes, title: 'My Class Timetable — Faculty', institutionName: 'Medical College ERP', showFaculty: false })}
            style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #334155', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'#334155', fontFamily:'inherit', flexShrink:0, marginTop:4 }}>
            🖨 Export PDF
          </button>
        )}
      </div>


      {/* Filter toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['all', 'PENDING', 'ONGOING', 'COMPLETED'].map(s => {
            const active = statusFilter === s;
            const m = STATUS_META[s];
            return (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{
                  padding: '6px 12px', borderRadius: 7,
                  border: `1.5px solid ${active && m ? m.color : '#e2e8f0'}`,
                  background: active && m ? m.bg : '#fff',
                  color: active && m ? m.color : '#374151',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>
                {s === 'all' ? 'All Classes' : (m?.label || s)}
              </button>
            );
          })}
        </div>
        {/* View mode toggle */}
        <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden', marginLeft:'auto' }}>
          {[['timetable','🗓 Timetable'],['calendar','📆 Calendar']].map(([mode,label]) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              style={{ padding:'6px 12px', border:'none', background:viewMode===mode?'#1d4ed8':'#fff', color:viewMode===mode?'#fff':'#374151', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'inherit', borderRight:'1px solid #e2e8f0' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Ongoing alert banner */}
      {counts.ONGOING > 0 && statusFilter === 'all' && (
        <div style={{ background: '#eff6ff', border: '2px solid #2563eb', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
          ▶ You have {counts.ONGOING} ongoing class{counts.ONGOING !== 1 ? 'es' : ''} — don't forget to mark {counts.ONGOING !== 1 ? 'them' : 'it'} as Completed when done!
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading your assigned classes…</div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 24px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          {classes.length === 0
            ? 'No classes assigned to you yet. Your HOD will assign classes once created by the Principal.'
            : `No ${statusFilter !== 'all' ? (STATUS_META[statusFilter]?.label + ' ') : ''}classes found.`}
        </div>
      ) : viewMode === 'timetable' ? (
        <ClassTimetableView
          classes={displayed}
          role="FACULTY"
          onStatusClick={setUpdateTarget}
        />
      ) : viewMode === 'calendar' ? (
        <ClassCalendarView
          classes={displayed}
          role="FACULTY"
          onStatusClick={setUpdateTarget}
        />
      ) : null}

      {updateTarget && (
        <StatusUpdateModal
          cs={updateTarget}
          onClose={() => setUpdateTarget(null)}
          onUpdated={() => { setUpdateTarget(null); load(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main FacultyDashboard component (from File 1, enhanced with File 2 tab)
───────────────────────────────────────────────────────────────────────────── */
const FacultyDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Dashboard data states
  const [stats, setStats] = useState({
    total_classes: 0,
    students_enrolled: 0,
    pending_evaluations: 0,
    upcoming_lectures: 0,
  });
  const [upcomingClasses,    setUpcomingClasses]    = useState([]);
  const [attendanceRecords,  setAttendanceRecords]  = useState([]);
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [studentPerformance, setStudentPerformance] = useState([]);
  const [pgStudents, setPgStudents] = useState([]);
  const [studentSubtab, setStudentSubtab] = useState('mbbs');
  const [yearFilter, setYearFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);
  const [selectedStudentForActivityModal, setSelectedStudentForActivityModal] = useState(null);
  const [departments,        setDepartments]        = useState([]);
  const [hodAssignments,     setHodAssignments]     = useState([]);

  // Modal states
  const [showAttendanceModal,        setShowAttendanceModal]        = useState(false);
  const [showCreateEvaluationModal,  setShowCreateEvaluationModal]  = useState(false);
  const [selectedClass,              setSelectedClass]              = useState(null);

  const [hasCMPermission, setHasCMPermission] = useState(false);

  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadDepartments();
    checkCMPermission();
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);

  const checkCMPermission = async () => {
    try {
      const data = await facultyThesisProposalAPI.checkCMPermission();
      setHasCMPermission(data.has_permission);
    } catch (err) {
      setHasCMPermission(false);
    }
  };

  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        if (upcomingClasses.length === 0)    loadUpcomingClasses();
        if (pendingEvaluations.length === 0) loadPendingEvaluations();
        break;
      case 'attendance':
        if (attendanceRecords.length === 0)  loadAttendanceRecords();
        break;
      case 'evaluations':
        if (pendingEvaluations.length === 0) loadPendingEvaluations();
        break;
      case 'students':
        if (studentPerformance.length === 0) loadStudentPerformance();
        break;
      default:
        break;
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, classesData, evaluationsData, hodAssignmentsData] = await Promise.all([
        facultyAPI.getDashboardStats(),
        facultyAPI.getUpcomingClasses(),
        facultyAPI.getPendingEvaluations(),
        facultyAPI.getActiveHODAssignments(),
      ]);

      console.log('Dashboard stats:', statsData);
      console.log('Upcoming classes:', classesData);
      console.log('Evaluations:', evaluationsData);
      console.log('HOD assignments:', hodAssignmentsData);

      setStats(statsData);

      const classesArray       = classesData?.results       || classesData       || [];
      const evaluationsArray   = evaluationsData?.results   || evaluationsData   || [];
      const hodAssignmentsArray = hodAssignmentsData?.results || hodAssignmentsData || [];

      setUpcomingClasses(Array.isArray(classesArray)        ? classesArray.slice(0, 3) : []);
      setPendingEvaluations(Array.isArray(evaluationsArray) ? evaluationsArray.slice(0, 3) : []);
      setHodAssignments(Array.isArray(hodAssignmentsArray)  ? hodAssignmentsArray : []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setStats({ total_classes: 0, students_enrolled: 0, pending_evaluations: 0, upcoming_lectures: 0 });
      setUpcomingClasses([]);
      setPendingEvaluations([]);
      setHodAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await departmentAPI.getDepartments();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadUpcomingClasses = async () => {
    try {
      const data = await facultyAPI.getUpcomingClasses();
      const classesArray = data?.results || data || [];
      setUpcomingClasses(Array.isArray(classesArray) ? classesArray.slice(0, 3) : []);
    } catch (error) {
      console.error('Error loading upcoming classes:', error);
      setUpcomingClasses([]);
    }
  };

  const loadAttendanceRecords = async () => {
    try {
      const data = await facultyAPI.getAttendanceRecords();
      const recordsArray = data?.results || data || [];
      setAttendanceRecords(Array.isArray(recordsArray) ? recordsArray : []);
    } catch (error) {
      console.error('Error loading attendance records:', error);
      setAttendanceRecords([]);
    }
  };

  const loadPendingEvaluations = async () => {
    try {
      const data = await facultyAPI.getPendingEvaluations();
      const evaluationsArray = data?.results || data || [];
      setPendingEvaluations(Array.isArray(evaluationsArray) ? evaluationsArray : []);
    } catch (error) {
      console.error('Error loading pending evaluations:', error);
      setPendingEvaluations([]);
    }
  };

  const loadStudentPerformance = async () => {
    try {
      const [mbbsRes, pgRes] = await Promise.allSettled([
        mbbsAPI.getFacultyMBBSStudents({ page_size: 2000 }),
        facultyAPI.getPGStudents().catch(() => [])
      ]);

      if (mbbsRes.status === 'fulfilled') {
        const arr = Array.isArray(mbbsRes.value) ? mbbsRes.value : (mbbsRes.value?.results || []);
        setStudentPerformance(arr);
      }
      if (pgRes.status === 'fulfilled') {
        const arr = Array.isArray(pgRes.value) ? pgRes.value : (pgRes.value?.results || []);
        setPgStudents(arr);
      }
    } catch (error) {
      console.error('Error loading student performance:', error);
    }
  };

  // ── Formatters (from File 1) ──
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDaysOfWeek = (daysArray) => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (!Array.isArray(daysArray) || daysArray.length === 0) return 'No days specified';
    return daysArray.sort().map(day => dayNames[day]).join(', ');
  };

  const handleOpenAttendanceModal = (classItem) => {
    setSelectedClass(classItem);
    setShowAttendanceModal(true);
  };

  if (loading) {
    return (
      <DashboardLayout title={`Welcome, Dr. ${user?.last_name}!`}>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`Welcome, Dr. ${user?.last_name}!`}>
      <div className="dashboard-container">

        {/* Stats Grid */}
        <div className="stats-grid">
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
            title="Total Classes"
            value={stats.total_classes}
            color="blue"
          />
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            title="Students Enrolled"
            value={stats.students_enrolled}
            color="green"
          />
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            }
            title="Pending Evaluations"
            value={stats.pending_evaluations}
            color="orange"
          />
          <StatCard
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            title="Upcoming Lectures"
            value={stats.upcoming_lectures}
            color="purple"
          />
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            {[
              { key: 'overview',            label: 'Overview' },
              { key: 'classes',             label: 'My Classes' },
              { key: 'evaluations',         label: 'Evaluations' },
              { key: 'students',            label: 'Students' },
              { key: 'opd',                 label: '🩺 OPD Queue' },
              { key: 'leave',               label: 'Leave Management' },
              { key: 'calendar',            label: '📅 Calendar' },
              { key: 'attendance',          label: 'Mark Attendance' },
              { key: 'request-payslip',     label: 'Request Payslip' },
              { key: 'library',             label: '📚 My Library' },
              ...(hasHostelAllocation ? [
                { key: 'hostel',            label: '🏨 Hostel' }
              ] : []),
              ...(hasCMPermission ? [
                { key: 'thesis-proposals',  label: '🎓 Thesis Review' },
                { key: 'ethics-clearance',  label: '⚖️ Ethics Clearance' }
              ] : [])
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">

          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="grid-2-col">

              {/* Upcoming Classes */}
              <div className="card">
                <div className="card-header">
                  <h2>Upcoming Classes</h2>
                  <button className="btn-link" onClick={() => setActiveTab('classes')}>View All</button>
                </div>
                <div className="card-body">
                  {upcomingClasses.length === 0 ? (
                    <p className="empty-state">No upcoming classes scheduled</p>
                  ) : (
                    upcomingClasses.map((classItem) => (
                      <div key={classItem.id} className="list-item">
                        <div className="list-item-icon class-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="list-item-content">
                          <h4>{classItem.subject} - {classItem.batch}</h4>
                          <p>{formatDate(classItem.date)} at {formatTime(classItem.start_time)} • {classItem.room}</p>
                        </div>
                        {!classItem.is_cancelled && (
                          <button className="btn-sm btn-outline" onClick={() => handleOpenAttendanceModal(classItem)}>
                            Mark Attendance
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Pending Evaluations */}
              <div className="card">
                <div className="card-header">
                  <h2>Pending Evaluations</h2>
                  <button className="btn-link" onClick={() => setActiveTab('evaluations')}>View All</button>
                </div>
                <div className="card-body">
                  {pendingEvaluations.length === 0 ? (
                    <p className="empty-state">No pending evaluations</p>
                  ) : (
                    pendingEvaluations.map((evaluation) => (
                      <div key={evaluation.id} className="list-item">
                        <div className="list-item-icon evaluation-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <div className="list-item-content">
                          <h4>{evaluation.title} - {evaluation.subject}</h4>
                          <p>{evaluation.submissions_count || 0} submissions • Deadline: {formatDate(evaluation.deadline)}</p>
                        </div>
                        <span className="badge badge-orange">Pending</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* HOD Class Assignments (overview card) */}
              {hodAssignments.length > 0 && (
                <div className="card full-width">
                  <div className="card-header">
                    <h2>Classes Assigned by HOD</h2>
                    <span className="badge badge-blue">
                      {hodAssignments.length} Active Assignment{hodAssignments.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Subject</th>
                            <th>Batch</th>
                            <th>Period</th>
                            <th>Schedule</th>
                            <th>Room</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hodAssignments.map((assignment) => (
                            <tr key={assignment.id}>
                              <td><strong>{assignment.subject}</strong></td>
                              <td>{assignment.batch}</td>
                              <td>{formatDate(assignment.start_date)} - {formatDate(assignment.end_date)}</td>
                              <td>
                                <div style={{ fontSize: '0.875rem' }}>
                                  {formatDaysOfWeek(assignment.days_of_week)}<br />
                                  {formatTime(assignment.start_time)} - {formatTime(assignment.end_time)}
                                </div>
                              </td>
                              <td>{assignment.room}</td>
                              <td>
                                <span className={`badge badge-${
                                  assignment.status === 'ACTIVE'  ? 'green' :
                                  assignment.status === 'PENDING' ? 'orange' : 'gray'
                                }`}>
                                  {assignment.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── My Classes — now powered by FacultyClassesTab ── */}
          {activeTab === 'classes' && (
            <FacultyClassesTab />
          )}


          {/* ── Evaluations ── */}
          {activeTab === 'evaluations' && (
            <div className="card">
              <div className="card-header">
                <h2>Evaluation Management</h2>
                <button className="btn-primary" onClick={() => setShowCreateEvaluationModal(true)}>
                  + Create Evaluation
                </button>
              </div>
              <div className="card-body">
                {pendingEvaluations.length === 0 ? (
                  <p className="empty-state">No evaluations found</p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Type</th>
                          <th>Subject</th>
                          <th>Batch</th>
                          <th>Deadline</th>
                          <th>Submissions</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingEvaluations.map((evaluation) => (
                          <tr key={evaluation.id}>
                            <td><strong>{evaluation.title}</strong></td>
                            <td>{evaluation.evaluation_type}</td>
                            <td>{evaluation.subject}</td>
                            <td>{evaluation.batch}</td>
                            <td>{formatDate(evaluation.deadline)}</td>
                            <td>{evaluation.submissions_count || 0}</td>
                            <td>
                              <span className={`badge badge-${
                                evaluation.status === 'PENDING'     ? 'orange' :
                                evaluation.status === 'IN_PROGRESS' ? 'blue'   : 'green'
                              }`}>
                                {evaluation.status}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button className="btn-sm btn-primary">Grade</button>
                                <button className="btn-sm btn-outline">View</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Students Tab with MBBS & PG Subtabs ── */}
          {activeTab === 'students' && (() => {
            const mbbsList = studentPerformance.filter(s => (s.profile?.course || s.course || 'MBBS') === 'MBBS');
            const pgList   = pgStudents.length > 0 ? pgStudents : studentPerformance.filter(s => (s.profile?.course || s.course) !== 'MBBS');

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Subtab Header Switcher Bar */}
                <div className="card" style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Students Directory & Roster</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>Select MBBS or Postgraduate student registry below</p>
                  </div>

                  <div style={{ display: 'flex', background: '#f1f5f9', padding: 4, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                    <button
                      onClick={() => setStudentSubtab('mbbs')}
                      style={{
                        padding: '7px 16px',
                        borderRadius: 7,
                        border: 'none',
                        background: studentSubtab === 'mbbs' ? '#ffffff' : 'transparent',
                        color: studentSubtab === 'mbbs' ? '#1d4ed8' : '#64748b',
                        fontWeight: studentSubtab === 'mbbs' ? 700 : 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        boxShadow: studentSubtab === 'mbbs' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <span>🎓 MBBS Students</span>
                      <span style={{ padding: '1px 6px', borderRadius: 10, background: studentSubtab === 'mbbs' ? '#dbeafe' : '#e2e8f0', color: studentSubtab === 'mbbs' ? '#1e40af' : '#475569', fontSize: 11, fontWeight: 800 }}>{mbbsList.length}</span>
                    </button>

                    <button
                      onClick={() => setStudentSubtab('pg')}
                      style={{
                        padding: '7px 16px',
                        borderRadius: 7,
                        border: 'none',
                        background: studentSubtab === 'pg' ? '#ffffff' : 'transparent',
                        color: studentSubtab === 'pg' ? '#1d4ed8' : '#64748b',
                        fontWeight: studentSubtab === 'pg' ? 700 : 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        boxShadow: studentSubtab === 'pg' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <span>🔬 Postgraduate (PG) Students</span>
                      <span style={{ padding: '1px 6px', borderRadius: 10, background: studentSubtab === 'pg' ? '#dbeafe' : '#e2e8f0', color: studentSubtab === 'pg' ? '#1e40af' : '#475569', fontSize: 11, fontWeight: 800 }}>{pgList.length}</span>
                    </button>
                  </div>
                </div>

                {/* SUBTAB 1: MBBS STUDENTS */}
                {studentSubtab === 'mbbs' && (() => {
                  const filteredMBBS = mbbsList.filter(student => {
                    if (studentSearchQuery && studentSearchQuery.trim()) {
                      const q = studentSearchQuery.toLowerCase();
                      const name = String(student.full_name || `${student.first_name || ''} ${student.last_name || ''}`).toLowerCase();
                      const sId = String(student.profile?.student_id || student.student_id || student.username || '').toLowerCase();
                      if (!name.includes(q) && !sId.includes(q)) return false;
                    }
                    if (yearFilter) {
                      const yr = String(student.profile?.mbbs_year || student.mbbs_year || student.year || '');
                      if (yr !== String(yearFilter)) return false;
                    }
                    return true;
                  });

                  return (
                    <div className="card">
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>🎓 MBBS Undergraduate Students Roster</h3>
                          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>Showing {filteredMBBS.length} of {mbbsList.length} total MBBS students across 1st to 4th years</p>
                        </div>
                        <div className="card-header-actions" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select
                            value={yearFilter}
                            onChange={e => setYearFilter(e.target.value)}
                            className="filter-select"
                            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 700 }}
                          >
                            <option value="">All MBBS Years</option>
                            <option value="1">1st Year (MBBS)</option>
                            <option value="2">2nd Year (MBBS)</option>
                            <option value="3">3rd Year (MBBS)</option>
                            <option value="4">4th Year (MBBS)</option>
                          </select>

                          <input
                            type="search"
                            value={studentSearchQuery}
                            onChange={e => setStudentSearchQuery(e.target.value)}
                            placeholder="🔍 Search name or student ID..."
                            className="search-input"
                            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 220 }}
                          />
                        </div>
                      </div>
                      <div className="card-body">
                        {filteredMBBS.length === 0 ? (
                          <p className="empty-state">No MBBS students found matching your filters</p>
                        ) : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Student ID</th>
                                  <th>Name</th>
                                  <th>Course</th>
                                  <th>MBBS Year</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredMBBS.map((student) => {
                                  const yrNum = student.profile?.mbbs_year || student.mbbs_year || student.year;
                                  const yrLabel = yrNum ? `${yrNum}${yrNum === 1 ? 'st' : yrNum === 2 ? 'nd' : yrNum === 3 ? 'rd' : 'th'} Year` : '—';

                                  return (
                                    <tr key={student.id}>
                                      <td>{student.profile?.student_id || student.student_id || student.username || 'N/A'}</td>
                                      <td><strong>{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.username}</strong></td>
                                      <td>
                                        <span className="badge badge-green">MBBS</span>
                                      </td>
                                      <td>
                                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8' }}>
                                          {yrLabel}
                                        </span>
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <button className="btn-sm btn-outline" onClick={() => setSelectedStudentForModal(student)}>View Details</button>
                                          <button
                                            className="btn-sm"
                                            style={{ background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', itemsCenter: 'center', gap: 4 }}
                                            onClick={() => setSelectedStudentForActivityModal(student)}
                                          >
                                            📋 Review Activities
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* SUBTAB 2: POSTGRADUATE (PG) STUDENTS */}
                {studentSubtab === 'pg' && (() => {
                  const isHOD = user?.role === 'HOD' || ['HEAD_OF_DEPARTMENT', 'PROFESSOR_HOD'].includes(user?.profile?.faculty_subrole);

                  const filteredPG = pgList.filter(student => {
                    if (studentSearchQuery && studentSearchQuery.trim()) {
                      const q = studentSearchQuery.toLowerCase();
                      const name = String(student.full_name || `${student.first_name || ''} ${student.last_name || ''}`).toLowerCase();
                      const sId = String(student.profile?.student_id || student.student_id || student.username || '').toLowerCase();
                      if (!name.includes(q) && !sId.includes(q)) return false;
                    }
                    if (courseFilter && student.course !== courseFilter && student.profile?.course !== courseFilter) return false;

                    if (isHOD) {
                      // HOD: Display only PG/SSPG students belonging to HOD's own department
                      const hodDeptId = user?.profile?.faculty_department?.id || user?.faculty_department_id;
                      const hodDeptName = (user?.profile?.faculty_department?.name || user?.department_name || '').toLowerCase();
                      const stdDeptId = student.profile?.student_department?.id || student.department_id;
                      const stdDeptName = (student.department_name || student.profile?.student_department?.name || '').toLowerCase();

                      if (hodDeptId && stdDeptId) {
                        if (String(hodDeptId) !== String(stdDeptId)) return false;
                      } else if (hodDeptName && stdDeptName) {
                        if (hodDeptName !== stdDeptName) return false;
                      }
                    } else {
                      // Non-HOD Faculty (Professor, Assoc Prof, Asst Prof, Sr Resident):
                      // Display ONLY PG/SSPG students who specifically selected this faculty member as assigned professor during registration
                      const isMyStudent = student.assigned_professor_id === user?.id ||
                        (student.assigned_professor_name && user?.first_name && student.assigned_professor_name.toLowerCase().includes(user.first_name.toLowerCase()));
                      if (!isMyStudent) return false;
                    }

                    return true;
                  });

                  return (
                    <div className="card">
                      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                            {isHOD ? '🔬 Department Postgraduate (PG) & Super Speciality Students Roster' : '🔬 My Assigned Postgraduate (PG) & Super Speciality Mentees'}
                          </h3>
                          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>
                            {isHOD
                              ? `Showing ${filteredPG.length} postgraduate residents belonging to your department`
                              : `Showing ${filteredPG.length} postgraduate residents who selected you as their Professor/Guide during registration`}
                          </p>
                        </div>
                        <div className="card-header-actions" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <select value={courseFilter} onChange={e => setCourseFilter(e.target.value)} className="filter-select" style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 600 }}>
                            <option value="">All PG Programs</option>
                            <option value="PG">PG (MD / MS)</option>
                            <option value="SUPER_SPECIALITY">Super Speciality (DM / MCh)</option>
                          </select>

                          <input
                            type="search"
                            value={studentSearchQuery}
                            onChange={e => setStudentSearchQuery(e.target.value)}
                            placeholder="🔍 Search name or student ID..."
                            className="search-input"
                            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 220 }}
                          />
                        </div>
                      </div>
                      <div className="card-body">
                        {filteredPG.length === 0 ? (
                          <p className="empty-state">
                            {isHOD
                              ? 'No Postgraduate (PG) students found in your department'
                              : 'No Postgraduate (PG) students have selected you as their Professor/Guide during registration'}
                          </p>
                        ) : (
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Student ID</th>
                                  <th>Name</th>
                                  <th>Course / Specialty</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredPG.map((student) => {
                                  const progName = student.course_display || student.course || student.profile?.course || 'PG Resident';

                                  return (
                                    <tr key={student.id}>
                                      <td>{student.profile?.student_id || student.student_id || student.username || 'N/A'}</td>
                                      <td>
                                        <strong>{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.username}</strong>
                                      </td>
                                      <td>
                                        <span className="badge badge-purple" style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#f3e8ff', color: '#6b21a8' }}>
                                          {progName}
                                        </span>
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <button className="btn-sm btn-outline" onClick={() => setSelectedStudentForModal(student)}>View Details</button>
                                          <button
                                            className="btn-sm"
                                            style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', itemsCenter: 'center', gap: 4 }}
                                            onClick={() => setSelectedStudentForActivityModal(student)}
                                          >
                                            📋 Review Activities
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Student Profile Detail Modal for Faculty */}
                {selectedStudentForModal && (
                  <div className="modal-overlay" onClick={() => setSelectedStudentForModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', width: '100%', maxWidth: 600, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', pb: 12, marginBottom: 16 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                            🎓 Student Details: {selectedStudentForModal.full_name || `${selectedStudentForModal.first_name || ''} ${selectedStudentForModal.last_name || ''}`}
                          </h3>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>ID: {selectedStudentForModal.profile?.student_id || selectedStudentForModal.student_id || selectedStudentForModal.username}</div>
                        </div>
                        <button onClick={() => setSelectedStudentForModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: 13 }}>
                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10 }}>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Course & Program</div>
                          <div style={{ fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                            {selectedStudentForModal.profile?.course || selectedStudentForModal.course || 'MBBS'}
                          </div>
                        </div>

                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10 }}>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Department</div>
                          <div style={{ fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                            {selectedStudentForModal.department_name || selectedStudentForModal.profile?.student_department?.name || 'General'}
                          </div>
                        </div>

                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10 }}>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Email Address</div>
                          <div style={{ fontWeight: 600, color: '#0f172a', marginTop: 4 }}>{selectedStudentForModal.email || '—'}</div>
                        </div>

                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 10 }}>
                          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Approval Status</div>
                          <div style={{ fontWeight: 700, color: '#166534', marginTop: 4 }}>
                            {selectedStudentForModal.approval_status || 'APPROVED'}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                        <button onClick={() => setSelectedStudentForModal(null)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Close</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}


          {/* ── OPD Queue ── */}
          {activeTab === 'opd' && (
            <OPDConsultationTab user={user} />
          )}

          {/* ── Leave Management ── */}
          {activeTab === 'leave' && (
            <div className="leave-management-section">
              <LeaveManagementSidebar />
            </div>
          )}

          {/* ── Calendar ── */}
          {activeTab === 'calendar' && (
            <div className="card" style={{ padding: 0, overflow: 'visible' }}>
              <Calendar />
            </div>
          )}

          {/* ── Mark Attendance ── */}
          {activeTab === 'attendance' && (
            <SubmitAttendance />
          )}

          {/* ── Request Payslip ── */}
          {activeTab === 'request-payslip' && (
            <SalarySlipRequest />
          )}

          {/* ── CM Review Panels ── */}
          {hasCMPermission && activeTab === 'thesis-proposals' && (
            <div className="card" style={{ padding: 20 }}>
              <FacultyCMReviews mode="thesis" />
            </div>
          )}
          {hasCMPermission && activeTab === 'ethics-clearance' && (
            <div className="card" style={{ padding: 20 }}>
              <FacultyCMReviews mode="ethics" />
            </div>
          )}
          {activeTab === 'library' && (
            <MyBorrowedBooks />
          )}
          {activeTab === 'hostel' && (
            <HostelUserPortalTab />
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {selectedStudentForActivityModal && (
        <StudentActivityModal
          student={selectedStudentForActivityModal}
          onClose={() => setSelectedStudentForActivityModal(null)}
          user={user}
        />
      )}
      {showAttendanceModal && selectedClass && (
        <AttendanceModal
          classData={selectedClass}
          onClose={() => { setShowAttendanceModal(false); setSelectedClass(null); }}
          onSuccess={() => {
            setShowAttendanceModal(false);
            setSelectedClass(null);
            loadAttendanceRecords();
            loadDashboardData();
          }}
        />
      )}

      {showCreateEvaluationModal && (
        <CreateEvaluationModal
          onClose={() => setShowCreateEvaluationModal(false)}
          onSuccess={() => {
            setShowCreateEvaluationModal(false);
            loadPendingEvaluations();
            loadDashboardData();
          }}
          departments={departments}
        />
      )}
    </DashboardLayout>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   AttendanceModal (from File 1)
───────────────────────────────────────────────────────────────────────────── */
const AttendanceModal = ({ classData, onClose, onSuccess }) => {
  const [students,   setStudents]   = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => { loadStudents(); }, []);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const data = await facultyAPI.getStudentsByBatch(classData.batch, classData.department);
      const studentsArray = data?.results || data || [];
      setStudents(Array.isArray(studentsArray) ? studentsArray : []);

      const initialAttendance = {};
      (Array.isArray(studentsArray) ? studentsArray : []).forEach(student => {
        initialAttendance[student.id] = { student_id: student.id, status: 'ABSENT', remarks: '' };
      });
      setAttendance(initialAttendance);
    } catch {
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange  = (studentId, status)  => setAttendance(prev => ({ ...prev, [studentId]: { ...prev[studentId], status } }));
  const handleRemarksChange = (studentId, remarks) => setAttendance(prev => ({ ...prev, [studentId]: { ...prev[studentId], remarks } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      await facultyAPI.markAttendance(classData.id, Object.values(attendance));
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Mark Attendance</h2>
            <p className="modal-subtitle">
              {classData.subject} - {classData.batch} | {classData.date}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="modal-body">
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading students...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              {students.length === 0 ? (
                <p className="empty-state">No students found for this batch</p>
              ) : (
                <div className="attendance-table-container">
                  <table className="attendance-table">
                    <thead>
                      <tr>
                        <th>Student ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id}>
                          <td>{student.profile?.student_id || 'N/A'}</td>
                          <td><strong>{student.first_name} {student.last_name}</strong></td>
                          <td>
                            <select
                              value={attendance[student.id]?.status || 'ABSENT'}
                              onChange={e => handleStatusChange(student.id, e.target.value)}
                              className="attendance-select"
                            >
                              <option value="PRESENT">Present</option>
                              <option value="ABSENT">Absent</option>
                              <option value="LATE">Late</option>
                              <option value="EXCUSED">Excused</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              value={attendance[student.id]?.remarks || ''}
                              onChange={e => handleRemarksChange(student.id, e.target.value)}
                              placeholder="Optional remarks"
                              className="remarks-input"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting || students.length === 0}>
                {submitting ? 'Saving...' : 'Save Attendance'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   CreateEvaluationModal (from File 1)
───────────────────────────────────────────────────────────────────────────── */
const CreateEvaluationModal = ({ onClose, onSuccess, departments }) => {
  const [formData, setFormData] = useState({
    title: '', evaluation_type: 'ASSIGNMENT', subject: '', batch: '',
    department: '', description: '', deadline: '', max_marks: '', total_students: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      await facultyAPI.createEvaluation(formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Evaluation</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}
            <div className="form-grid">

              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input type="text" id="title" name="title" placeholder="e.g., Midterm Assignment"
                  value={formData.title} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="evaluation_type">Type *</label>
                <select id="evaluation_type" name="evaluation_type" value={formData.evaluation_type} onChange={handleChange} required>
                  <option value="ASSIGNMENT">Assignment</option>
                  <option value="PROJECT">Project</option>
                  <option value="PRESENTATION">Presentation</option>
                  <option value="QUIZ">Quiz</option>
                  <option value="CASE_STUDY">Case Study</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="batch">Batch *</label>
                <input type="text" id="batch" name="batch" placeholder="e.g., MBBS Year 1"
                  value={formData.batch} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="department">Department *</label>
                <select id="department" name="department" value={formData.department} onChange={handleChange} required>
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="deadline">Deadline *</label>
                <input type="date" id="deadline" name="deadline" value={formData.deadline} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="max_marks">Maximum Marks *</label>
                <input type="number" id="max_marks" name="max_marks" min="0" step="0.01"
                  value={formData.max_marks} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="total_students">Total Students</label>
                <input type="number" id="total_students" name="total_students" min="0"
                  value={formData.total_students} onChange={handleChange} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea id="description" name="description" rows="4"
                value={formData.description} onChange={handleChange} required />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Evaluation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FacultyDashboard;